import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useApi } from '../../hooks/useApi';
import { getKids } from '../../lib/api/kids';
import { getMyHouseholds } from '../../lib/api/households';
import KidCard from './KidCard';
import AddKidModal from './AddKidModal';
import EditKidModal from './EditKidModal';
import AddEntryModal from '../KidDetail/AddEntryModal';
import Button from '../../components/common/Button';
import SafeIcon from '../../common/SafeIcon';
import Modal from '../../components/common/Modal';

export default function Dashboard() {
  const { user, switchHousehold } = useAuth();
  const { execute: fetchKids, loading } = useApi(getKids);
  const { execute: fetchHouseholds } = useApi(getMyHouseholds);
  
  const [kids, setKids] = useState([]);
  const [households, setHouseholds] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [showSwitcher, setShowSwitcher] = useState(false);
  const [editingKid, setEditingKid] = useState(null);
  const [quickAction, setQuickAction] = useState(null);

  const loadData = async () => {
    if (!user?.householdId) return;
    try {
      // getKids now automatically triggers the allowance sync RPC
      const kidData = await fetchKids(user.householdId, user.id);
      setKids(kidData.filter(k => !k.is_archived));
      
      const hData = await fetchHouseholds(user.id);
      setHouseholds(hData);
    } catch(e) {
      console.error(e);
    }
  };

  useEffect(() => {
    loadData();
  }, [user?.householdId]);

  const handleSwitch = (h) => {
    switchHousehold(h.id, h.role);
    setShowSwitcher(false);
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500 pb-20">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center bg-white p-5 rounded-2xl shadow-sm border border-gray-100 gap-4">
        <div>
          <div className="flex items-center space-x-2 mb-1">
            <h1 className="text-2xl font-black text-gray-900">Dashboard</h1>
            {households.length > 1 && (
              <button 
                onClick={() => setShowSwitcher(true)}
                className="flex items-center space-x-1 px-2 py-0.5 bg-indigo-50 text-indigo-600 rounded-lg text-[10px] font-bold uppercase tracking-wider hover:bg-indigo-100 transition-colors"
              >
                <SafeIcon icon="RefreshCw" className="w-3 h-3" />
                <span>Switch Family</span>
              </button>
            )}
          </div>
          <p className="text-sm text-gray-500 flex items-center">
            <SafeIcon icon="Home" className="w-3.5 h-3.5 mr-1.5 text-indigo-400" />
            Active Household: 
            <span className="font-bold text-gray-700 ml-1">
              {households.find(h => h.id === user?.householdId)?.name || 'Loading...'}
            </span>
          </p>
        </div>
        <Button onClick={() => setShowAdd(true)} icon="Plus" className="w-full sm:w-auto shadow-lg shadow-indigo-100">
          Add Kid
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center p-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {kids.map(kid => (
            <KidCard 
              key={kid.id} 
              kid={kid} 
              onUpdate={loadData}
              onEdit={(k) => setEditingKid(k)}
              onQuickAction={(k, type) => setQuickAction({ kid: k, type })}
            />
          ))}

          {kids.length === 0 && (
            <div className="col-span-full text-center py-20 bg-white rounded-2xl border-2 border-dashed border-gray-200">
              <div className="bg-gray-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <SafeIcon icon="Users" className="text-gray-300 w-10 h-10" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">No kids found in this family</h3>
              <p className="text-gray-500 mb-8 max-w-xs mx-auto">
                Add a child profile to start tracking their pocket money and chores.
              </p>
              <Button variant="primary" onClick={() => setShowAdd(true)} icon="Plus">
                Create First Profile
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      {showSwitcher && (
        <Modal title="Switch Household" onClose={() => setShowSwitcher(false)}>
          <div className="space-y-3">
            {households.map(h => (
              <button
                key={h.id}
                onClick={() => handleSwitch(h)}
                className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all ${
                  user?.householdId === h.id 
                  ? 'border-indigo-500 bg-indigo-50' 
                  : 'border-gray-100 bg-gray-50 hover:border-gray-200'
                }`}
              >
                <div className="flex items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-4 ${
                    user?.householdId === h.id ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-400'
                  }`}>
                    <SafeIcon icon="Home" className="w-5 h-5" />
                  </div>
                  <div className="text-left font-bold text-gray-900">{h.name}</div>
                </div>
                {user?.householdId === h.id && <SafeIcon icon="Check" className="text-indigo-600 w-5 h-5" />}
              </button>
            ))}
          </div>
        </Modal>
      )}

      {showAdd && <AddKidModal onClose={() => setShowAdd(false)} onSuccess={loadData} />}
      {editingKid && <EditKidModal kid={editingKid} onClose={() => setEditingKid(null)} onSuccess={loadData} />}
      {quickAction && <AddEntryModal kidId={quickAction.kid.id} initialType={quickAction.type} onClose={() => setQuickAction(null)} onSuccess={loadData} />}
    </div>
  );
}